import { graphql } from '@/__generated__';
import { useCacheMonitor } from '@/context/DiscoveryUrqlProvider';
import { useQueryStable } from '../useQueryStable';
import { GetAppliedNode, GetDefinitionNode } from './testUtil';

type Result = ReturnType<typeof useSemanticModelByEnvironment>[0];

export type SemanticModelResponse = NonNullable<Result['data']>;

export type RequestedSemanticModelAppliedNode = GetAppliedNode<
  'semanticModels',
  SemanticModelResponse
>;

export type RequestedSemanticModelDefinitionNode = GetDefinitionNode<
  'semanticModels',
  SemanticModelResponse
>;

// https://github.com/dbt-labs/codex-api/blob/main/schema.graphql
// https://github.com/dbt-labs/codex-api/blob/main/frontend/package.json#L33
// https://github.com/dbt-labs/codex-api/blob/main/frontend/codegen.yml
// generates: https://github.com/dbt-labs/codex-api/blob/main/frontend/src/typings/codex.ts
export const GetSemanticModelByUniqueId = graphql(/* GraphQL */ `
  query SemanticModel($environmentId: BigInt!, $uniqueId: String!, $userId: BigInt) {
    environment(id: $environmentId, userId: $userId) {
      definition {
        lastUpdatedAt
        semanticModels(first: 1, filter: { uniqueIds: [$uniqueId] }) {
          edges {
            node {
              fqn
              uniqueId
              name
              tags
              description
              packageName
              filePath
              patchPath
              projectId
              runGeneratedAt
              parents {
                name
                uniqueId
                resourceType
                __typename
                ... on ModelDefinitionNestedNode {
                  access
                  alias
                }
              }
              children {
                uniqueId
              }
              dimensions {
                description
                name
                type
                typeParams
              }
              entities {
                description
                name
                type
              }
              measures {
                agg
                createMetric
                description
                expr
                name
              }
            }
          }
        }
      }
    }
  }
`);

type SemanticModelByEnvironmentParams = {
  environmentId: number | undefined;
  uniqueId: string;
  userId: number | 'loading' | null;
  pause?: boolean;
};

export const useSemanticModelByEnvironment = ({
  environmentId = 0,
  uniqueId,
  userId,
  pause,
}: SemanticModelByEnvironmentParams) => {
  const query = useQueryStable({
    query: GetSemanticModelByUniqueId,
    variables: { environmentId, uniqueId, userId: userId as number | null },
    pause: pause || !environmentId || !uniqueId || userId === 'loading',
  });

  useCacheMonitor({
    definition: query[0].data?.environment.definition?.lastUpdatedAt,
  });

  return query;
};
